import {Injectable} from "@angular/core";
import {Http} from "@angular/http";
import {
    BASE_CONSTANTS,
    BASE_URLS,
    SERVICE_URLS,
    QUERY_PARAMS,
    TCI_APP_DETAIL_MENU_TYPE,
    TCI_COMPONENT_MODE,
    ITCIMetrics,
    ITCIApplication,
    ITCIApplicationEndpoint,
    TCITenantAppType,
    TCITenantSelectorType
} from "@tci/common";
import {
    NgAppChangeNotificationService,
    ITCIAppRefreshNotification
} from "../../../ng-shared/ng-services/ng-app-change-notification.service";
import {BaseAppDetailMenuComponentInput} from "../../../ng-app-details/ng-app-details-menu";
import {TCITenantMenuServices} from "../../../ng-tenant/tci-tenant.menu";

import {Observable, Subject, Subscription} from "rxjs";
import * as lodash from "lodash";

@Injectable()
export class BaseAppListPeekService {

    private static instance: BaseAppListPeekService = null;

    private metrics$: { [key: string]: Observable<ITCIMetrics> } = {};

    private appRefreshSub: Subscription;
    private appRefresh$: Subject<ITCIAppRefreshNotification> = new Subject();

    private metricsRefresh$: Subject<string> = new Subject();

    /**
     * constructor
     * @param envVarsService
     * @param websocketService
     */
    constructor(private http: Http, private notificationSvc: NgAppChangeNotificationService, private tenantMenuSrv: TCITenantMenuServices) {
        this.appRefreshSub = notificationSvc.getRefreshSubject()
            .subscribe(refr => this.appRefresh$.next(refr));
    }

    public static getInstance(http: Http, notificationSvc: NgAppChangeNotificationService, tenantMenuSrv: TCITenantMenuServices) {
        if (!BaseAppListPeekService.instance) {
            BaseAppListPeekService.instance = new BaseAppListPeekService(http, notificationSvc, tenantMenuSrv);
        }
        return BaseAppListPeekService.instance;
    }

    getAppMetrics(app: ITCIApplication): Observable<ITCIMetrics> {
        if (!this.metrics$[app.id]) {
            const url = `${SERVICE_URLS.METRICS_RECORDS}/${app.id}?` +
                `${QUERY_PARAMS.APP_TYPE}=${app.type.toString()}&${QUERY_PARAMS.METRICS_CATEGORY}=appPeek&${QUERY_PARAMS.ON_PREMISE}=${app.onPremise ? "true" : "false"}`;
            this.metrics$[app.id] = this.http.get(url)
                .map(res => res.json())
                .do(() => delete this.metrics$[app.id])
                .share();
        }

        return this.metrics$[app.id];
    }

    getLastUpdateInfo(app: ITCIApplication): Observable<ITCIAppRefreshNotification> {
        return this.notificationSvc.getLastUpdateInfo(app);
    }

    /**
     * showExecutionsData
     * toggle the display of the success/failed execution info
     * (left column of peek view)
     * @param app
     */
    showExecutionsData(app: ITCIApplication): boolean {
        return !TCITenantAppType.node.equals(app.type); // Node does not display it
    }

    getNonPingEndpoints(appId: string): Observable<ITCIApplicationEndpoint[]> {
        const url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.APPS}/${appId}/endpoints?sandboxId=MyDefaultSandbox`;

        return this
            .http
            .get(url)
            .map(res => res.json() || []);
    }

    getAppRefreshSubject() {
        return this.appRefresh$;
    }

    stopRefreshSubscription() {
        this.appRefreshSub.unsubscribe();
    }

    getAppEndpointsRoute(input: BaseAppDetailMenuComponentInput): Observable<{ route: string[], queryParams: Object }> {
        const queryParams = {};
        queryParams[QUERY_PARAMS.APP_TYPE] = input.tenantAppType.toString();

        // Generate an input for the menu service based in the incoming one
        const endpointsSel = TCITenantSelectorType.TCI_APP_DETAIL_ENDPOINTS;
        const menuInput = lodash.cloneDeep(input);
        menuInput.componentSelector = endpointsSel;

        return this.tenantMenuSrv.getAppDetailMenuItems(menuInput.tenantAppType, TCI_APP_DETAIL_MENU_TYPE.MAIN_MENU, menuInput)
            .map(res => res && res.length > 0 ? res : [])
            .map(res => res.find(r => r.getComponentSelectorType().isEqualTo(endpointsSel.toString())))
            .map(res => {
                const routeId: string = res ? res.getRouteId() : QUERY_PARAMS.DEFAULT_ROUTE;
                return {route: ["details", routeId, menuInput.getApplication().id], queryParams};
            });
    }

    getMetricsRefreshSubj(): Subject<string> {
        return this.metricsRefresh$;
    }

    refreshMetrics(appId: string): void {
        this.metricsRefresh$.next(appId);
    }
}
